import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import GameCanvas from "./GameCanvas";
import Modal from "./Modal";
import { LoadingSpinner } from "./ErrorHandling";
import { ControlButtons } from "./ControlButtons";
import StatsBar from "./StatsBar";
import SettingsModal from "./SettingsModal";
import PlayerListModal from "./PlayerListModal";
import ActionBar from "./ActionBar";
import ArrowPanel from "./ArrowPanel";
import MobileActionDock from "./MobileActionDock";
import ContextPanel from "./ContextPanel";
import { useAuth } from "../context/AuthContext";
import { isDiscordActivity } from "../utils/discord";

import { useMobileLayout } from "../hooks/useMobileLayout";
import { useMapLoader } from "../hooks/useMapLoader";
import { useRoomJoin } from "../hooks/useRoomJoin";
import { useGameState } from "../hooks/useGameState";
import { useArrowSystem } from "../hooks/useArrowSystem";
import { useGameActions } from "../hooks/useGameActions";

const sameOwner = (ownerId, currentUserId) =>
  ownerId != null &&
  currentUserId != null &&
  String(ownerId) === String(currentUserId);

const Game = ({ discordRoomId }) => {
  // Get game room ID from URL params or Discord prop.
  const { id: paramId } = useParams();
  const id = discordRoomId || paramId;
  const isDiscord = isDiscordActivity();

  const routerNavigate = useNavigate();
  const navigate = isDiscord ? () => {} : routerNavigate;

  // ----------------------------
  // Login and credentials state
  // ----------------------------
  const {
    user,
    profile,
    loading: authLoading,
    loginWithGoogle,
  } = useAuth();
  const userId = user?.id || null;

  // ----------------------------
  // Modal and cell selection state
  // ----------------------------
  const [showSettings, setShowSettings] = useState(false);
  const [showPlayerList, setShowPlayerList] = useState(false);
  const [actionModal, setActionModal] = useState(null);
  const [foundingNation, setFoundingNation] = useState(false);
  const [buildingStructure, setBuildingStructure] = useState(null);
  const [isDefeated, setIsDefeated] = useState(false);
  const [isSpectating, setIsSpectating] = useState(false);
  const [hasFounded, setHasFounded] = useState(false);
  const [attackPercent, setAttackPercent] = useState(0.25);
  const [troopTarget, setTroopTarget] = useState(0.2);
  const [uiMode, setUiMode] = useState("idle");
  const [selectedCellInfo, setSelectedCellInfo] = useState(null);

  // Arrow drawing state (managed here because exitModes/enterDrawMode need it)
  const [drawingArrowType, setDrawingArrowType] = useState(null); // "attack" or "defend"
  const [currentArrowPath, setCurrentArrowPath] = useState([]); // [{x, y}, ...]

  const actionModalRef = useRef(actionModal);
  const isDefeatedRef = useRef(isDefeated);
  const hasFoundedRef = useRef(hasFounded);
  const isSpectatingRef = useRef(isSpectating);
  const canvasHostRef = useRef(null);
  const handleEndGameRef = useRef(null);

  useEffect(() => {
    actionModalRef.current = actionModal;
  }, [actionModal]);

  useEffect(() => {
    isDefeatedRef.current = isDefeated;
  }, [isDefeated]);

  useEffect(() => {
    hasFoundedRef.current = hasFounded;
  }, [hasFounded]);

  useEffect(() => {
    isSpectatingRef.current = isSpectating;
  }, [isSpectating]);

  // ----------------------------
  // UI mode helpers
  // ----------------------------
  const exitModes = () => {
    setFoundingNation(false);
    setBuildingStructure(null);
    setDrawingArrowType(null);
    setCurrentArrowPath([]);
    setUiMode("idle");
    setSelectedCellInfo(null);
  };

  const startFoundNation = () => {
    setIsSpectating(false);
    setFoundingNation(true);
    setBuildingStructure(null);
    setUiMode("foundNation");
    setSelectedCellInfo(null);
  };

  const enterDrawMode = (type) => {
    setFoundingNation(false);
    setBuildingStructure(null);
    setDrawingArrowType(type);
    setUiMode(type === "attack" ? "drawAttack" : "drawDefend");
    setSelectedCellInfo(null);
  };

  const setMode = (nextMode) => {
    if (nextMode === "idle") {
      exitModes();
      return;
    }
    if (nextMode === "pan") {
      setFoundingNation(false);
      setBuildingStructure(null);
      setDrawingArrowType(null);
      setCurrentArrowPath([]);
      setSelectedCellInfo(null);
    }
    setUiMode(nextMode);
  };

  const handleBuildStructure = (type) => {
    if (!isRoomStarted) return;
    setBuildingStructure(type);
    setFoundingNation(false);
    setDrawingArrowType(null);
    setCurrentArrowPath([]);
    setUiMode("buildStructure");
    setSelectedCellInfo(null);
  };

  const handleInspectCell = (info) => {
    setSelectedCellInfo(info);
  };

  const handleCancelBuild = () => {
    setBuildingStructure(null);
    if (uiMode === "buildStructure") {
      setUiMode("idle");
    }
  };

  // ----------------------------
  // Mobile layout hook
  // ----------------------------
  const {
    isMobile,
    statsBarHeight,
    setStatsBarHeight,
    mobileDockHeight,
    setMobileDockHeight,
    stageViewport,
    discordTopOffset,
    discordBottomOffset,
    controlButtonsTopOffset,
    arrowPanelTopOffset,
    arrowPanelBottomOffset,
    contextPanelBottomOffset,
    topScrimHeight,
  } = useMobileLayout({ isDiscord, canvasHostRef });

  // ----------------------------
  // Map loader hook
  // ----------------------------
  const {
    mapMetadata,
    loadedRows,
    mappings,
    mapGrid,
    isMapLoaded,
    regionData,
    config,
  } = useMapLoader({ id, userId });

  // ----------------------------
  // Room join hook
  // ----------------------------
  // We need applyDeltaGameStateRef before useGameState, but useRoomJoin also
  // needs it. We'll create a forwarding ref here.
  const applyDeltaGameStateRef = useRef(null);

  const {
    joinCode,
    setJoinCode,
    joinError,
    isJoining,
    hasJoined,
    setHasJoined,
    handleJoinSubmit,
  } = useRoomJoin({
    id,
    userId,
    user,
    isDiscord,
    navigate,
    authLoading,
    loginWithGoogle,
    applyDeltaGameStateRef,
  });

  // ----------------------------
  // Game state hook
  // ----------------------------
  const {
    gameState,
    setGameState,
    userState,
    setUserState,
    wsConnected,
    paused,
    setPaused,
    error,
    setError,
    combatFlashes,
    setCombatFlashes,
    lastWsUpdateAtRef,
    applyDeltaGameStateRef: gameStateApplyRef,
    requestFullStateRef,
  } = useGameState({
    id,
    userId,
    hasJoined,
    setHasJoined,
    isDiscord,
    navigate,
    config,
    setActionModal,
    setFoundingNation,
    setHasFounded,
    setIsSpectating,
    setIsDefeated,
    actionModalRef,
    isDefeatedRef,
    hasFoundedRef,
    isSpectatingRef,
    handleEndGameRef,
    actionModal,
    exitModes,
    setSelectedCellInfo,
  });

  // Keep the forwarding ref in sync so useRoomJoin can call applyDeltaGameState.
  // Done during render (not in useEffect) so it's available before any effects fire.
  applyDeltaGameStateRef.current = gameStateApplyRef.current;

  // ----------------------------
  // Derived room state
  // ----------------------------
  const allowRefound = config?.territorial?.allowRefound !== false;
  const roomStatus = gameState?.roomStatus || "open";
  const roomPlayers = gameState?.players || [];
  const isRoomStarted = roomStatus === "open" || roomStatus === "paused";
  const isRoomLobby = roomStatus === "lobby";
  const isRoomCreator = gameState?.roomCreator === userId;
  const readyPlayerCount = roomPlayers.filter((player) => player.ready).length;

  // ----------------------------
  // Game actions hook
  // ----------------------------
  const {
    handleFoundNation,
    handleBuildCity,
    handlePauseGame,
    handleUnPauseGame,
    handleEndGame,
    handleQuitGame,
    handleSetTroopTarget: handleSetTroopTargetRaw,
    handleStartRoom,
    isStartingRoom,
  } = useGameActions({
    id,
    userId,
    hasJoined,
    gameState,
    setGameState,
    config,
    navigate,
    isRoomStarted,
    isRoomCreator,
    isRoomLobby,
    setFoundingNation,
    setBuildingStructure,
    setIsDefeated,
    setIsSpectating,
    setActionModal,
    setHasFounded,
    setHasJoined,
    setUserState,
    setError,
    setUiMode,
    setPaused,
  });

  // Wire handleEndGameRef for the circular dependency with useGameState
  useEffect(() => {
    handleEndGameRef.current = handleEndGame;
  }, [handleEndGame]);

  // Wrap handleSetTroopTarget to pass setTroopTarget
  const lastSentTroopTargetRef = useRef(null);
  const handleSetTroopTarget = (newTarget) => {
    lastSentTroopTargetRef.current = newTarget;
    handleSetTroopTargetRaw(newTarget, setTroopTarget);
  };

  // Keep the mobilization slider in sync with the server. The server can
  // silently reduce troopTarget (starvation auto-demobilization) — adopt its
  // value and tell the player when that happens.
  const serverTroopTarget = userState?.troopTarget;
  const troopTargetSyncedRef = useRef(false);
  useEffect(() => {
    if (serverTroopTarget == null) return;
    if (Math.abs(serverTroopTarget - troopTarget) <= 0.011) {
      troopTargetSyncedRef.current = true;
      return;
    }
    const isEchoOfMySet =
      lastSentTroopTargetRef.current != null &&
      Math.abs(lastSentTroopTargetRef.current - serverTroopTarget) <= 0.011;
    const wasReduction = serverTroopTarget < troopTarget - 0.011;
    setTroopTarget(serverTroopTarget);
    if (wasReduction && !isEchoOfMySet && troopTargetSyncedRef.current) {
      setError(
        `Mobilization was reduced to ${Math.round(serverTroopTarget * 100)}% — food or gold ran out.`
      );
    }
    troopTargetSyncedRef.current = true;
  }, [serverTroopTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  // Errors auto-dismiss after a few seconds.
  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(null), 6000);
    return () => clearTimeout(timer);
  }, [error, setError]);

  // ----------------------------
  // Arrow system hook
  // ----------------------------
  const {
    activeAttackArrows,
    activeDefendArrow,
    handleStartDrawArrow,
    handleCancelArrow,
    handleArrowPathUpdate,
    handleSendArrow,
    handleClearActiveArrow,
    handleReinforceArrow,
    handleRetreatArrow,
  } = useArrowSystem({
    id,
    userId,
    hasJoined,
    gameState,
    config,
    isRoomStarted,
    attackPercent,
    drawingArrowType,
    setDrawingArrowType,
    currentArrowPath,
    setCurrentArrowPath,
    enterDrawMode,
    setUiMode,
    uiMode,
    setError,
  });

  // ----------------------------
  // Player nation derived state
  // ----------------------------
  const playerNation = gameState?.gameState?.nations?.find(
    (n) => sameOwner(n.owner, userId) && n.status !== "defeated"
  );
  const playerResources = playerNation?.resources || {};
  const playerTroopCount = playerNation?.troopCount ?? 0;
  // Cache density map — server throttles to every 5 ticks, use last known between updates
  const lastDensityMapRef = React.useRef(null);
  if (playerNation?.troopDensityMap) {
    lastDensityMapRef.current = playerNation.troopDensityMap;
  }
  const playerTroopDensityMap = lastDensityMapRef.current;

  // ----------------------------
  // Helper for determining player (nation) colors.
  // ----------------------------
  // Stable key that only changes when the nation owner/color list changes
  const nationColorKey = React.useMemo(() => {
    return (gameState?.gameState?.nations || [])
      .filter((n) => n?.owner)
      .sort((a, b) => (a.owner || "").localeCompare(b.owner || ""))
      .map((n) => n.owner + "|" + (n.color || ""))
      .join(",");
  }, [gameState]);

  const nationColors = React.useMemo(() => {
    const palette = [
      "#FF5733",
      "#33FF57",
      "#3357FF",
      "#FF33A8",
      "#A833FF",
      "#33FFF0",
      "#FFC133",
      "#FF3333",
      "#33FF33",
      "#3333FF",
      "#FF8C42",
      "#4ADEDE",
      "#F72585",
      "#3A86FF",
      "#2A9D8F",
    ];
    const nations = (gameState?.gameState?.nations || [])
      .filter((n) => n?.owner)
      .sort((a, b) => (a.owner || "").localeCompare(b.owner || ""));
    const colorMap = {};
    let idx = 0;
    nations.forEach((nation) => {
      if (colorMap[nation.owner]) return;
      if (nation.color) {
        colorMap[nation.owner] = nation.color;
        return;
      }
      if (idx < palette.length) {
        colorMap[nation.owner] = palette[idx];
      } else {
        const hue = (idx * 137.508) % 360;
        colorMap[nation.owner] = `hsl(${hue.toFixed(0)}, 75%, 55%)`;
      }
      idx += 1;
    });
    return colorMap;
  }, [nationColorKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stable key for nation labels — only recompute when owner/name list changes
  const nationLabelKey = React.useMemo(() => {
    return (gameState?.gameState?.nations || [])
      .filter((n) => n?.owner)
      .map((n) => n.owner + "|" + (n.nationName || n.displayName || ""))
      .join(",");
  }, [gameState]);

  const nationLabels = React.useMemo(() => {
    const labels = {};
    (gameState?.gameState?.nations || []).forEach((nation) => {
      if (nation.owner) {
        labels[nation.owner] =
          nation.nationName || nation.displayName || nation.owner;
      }
    });
    return labels;
  }, [nationLabelKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const getNationColor = (nation) => {
    if (!nation?.owner) return "#999999";
    return nation.color || nationColors[nation.owner] || "#999999";
  };

  return (
    <div
      className="relative overflow-hidden select-none"
      style={{
        height: "100dvh",
        minHeight: "100vh",
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        userSelect: "none",
      }}
    >
      {topScrimHeight > 0 && (
        <div
          className="pointer-events-none absolute left-0 right-0 top-0 z-30 bg-gray-900/90"
          style={{ height: `${topScrimHeight}px` }}
        />
      )}
      {error && (
        <div className="fixed left-1/2 top-16 z-50 flex max-w-md -translate-x-1/2 items-center gap-3 rounded-lg border border-red-700 bg-red-900/95 px-4 py-2 text-sm text-white shadow-lg">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="font-bold text-red-300 hover:text-white"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}
      <ControlButtons
        onOpenSettings={() => setShowSettings(true)}
        onOpenPlayerList={() => setShowPlayerList(true)}
        topOffset={controlButtonsTopOffset}
        isMobile={isMobile}
      />
      {!isDefeated && (
        <StatsBar
          gameState={gameState}
          userId={userId}
          topOffset={discordTopOffset}
          isMobile={isMobile}
          onHeightChange={setStatsBarHeight}
        />
      )}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        gameState={gameState}
        userId={userId}
        profile={profile}
        paused={paused}
        onPause={handlePauseGame}
        onUnpause={handleUnPauseGame}
        onEndGame={handleEndGame}
        onLeaveGame={handleQuitGame}
        onBackToGameRooms={() => navigate("/rooms")}
      />
      <PlayerListModal
        isOpen={showPlayerList}
        onClose={() => setShowPlayerList(false)}
        gameState={gameState}
        getNationColor={getNationColor}
      />
      {/* Main Content Area */}
      <div ref={canvasHostRef} className="absolute inset-0">
        {!isMapLoaded ? (
          <div className="flex flex-col items-center justify-center h-full bg-gray-800">
            <LoadingSpinner />
            {mapMetadata && (
              <div className="text-white mt-4">
                Loading map...{" "}
                {Math.min(
                  ((loadedRows / mapMetadata.height) * 100).toFixed(0),
                  100
                )}
                %
              </div>
            )}
          </div>
        ) : (
          <GameCanvas
            mapMetadata={mapMetadata}
            mapGrid={mapGrid}
            mappings={mappings}
            gameState={gameState}
            userId={userId}
            nationColors={nationColors}
            config={config}
            foundingNation={foundingNation}
            onFoundNation={handleFoundNation}
            buildingStructure={buildingStructure}
            onBuildCity={handleBuildCity}
            onCancelBuild={handleCancelBuild}
            drawingArrowType={drawingArrowType}
            onStartDrawArrow={handleStartDrawArrow}
            currentArrowPath={currentArrowPath}
            onArrowPathUpdate={handleArrowPathUpdate}
            onSendArrow={handleSendArrow}
            onCancelArrow={handleCancelArrow}
            activeAttackArrows={activeAttackArrows}
            activeDefendArrow={activeDefendArrow}
            uiMode={uiMode}
            isMobile={isMobile}
            onInspectCell={handleInspectCell}
            onError={setError}
            troopDensityMap={playerTroopDensityMap}
            combatFlashes={combatFlashes}
            setCombatFlashes={setCombatFlashes}
            regionData={regionData}
            isDiscord={isDiscord}
            stageWidth={stageViewport.width}
            stageHeight={stageViewport.height}
          />
        )}
      </div>
      {!isMobile && (
        <ActionBar
          onFoundNation={startFoundNation}
          userState={userState}
          hasFounded={hasFounded}
          isSpectating={isSpectating}
          allowRefound={allowRefound}
          attackPercent={attackPercent}
          setAttackPercent={setAttackPercent}
          troopTarget={troopTarget}
          onSetTroopTarget={handleSetTroopTarget}
          troopCount={playerTroopCount}
          drawingArrowType={drawingArrowType}
          onStartDrawArrow={handleStartDrawArrow}
          onCancelArrow={handleCancelArrow}
          activeAttackArrows={activeAttackArrows}
          activeDefendArrow={activeDefendArrow}
          maxAttackArrows={config?.territorial?.maxAttackArrows ?? 3}
          onBuildStructure={handleBuildStructure}
          playerResources={playerResources}
          buildCosts={config?.buildCosts?.structures}
          uiMode={uiMode}
          onSetMode={setMode}
          onExitMode={exitModes}
          arrowCosts={config?.arrowCosts}
          gameState={gameState}
          isRoomStarted={isRoomStarted}
          canStartRoom={isRoomCreator && isRoomLobby}
          onStartRoom={handleStartRoom}
          isStartingRoom={isStartingRoom}
          readyPlayerCount={readyPlayerCount}
          totalPlayers={roomPlayers.length}
        />
      )}
      {isMobile && (
        <MobileActionDock
          onFoundNation={startFoundNation}
          hasFounded={hasFounded}
          isSpectating={isSpectating}
          allowRefound={allowRefound}
          attackPercent={attackPercent}
          setAttackPercent={setAttackPercent}
          uiMode={uiMode}
          drawingArrowType={drawingArrowType}
          onSetMode={setMode}
          onStartDrawArrow={handleStartDrawArrow}
          onCancelArrow={handleCancelArrow}
          onBuildStructure={handleBuildStructure}
          playerResources={playerResources}
          buildCosts={config?.buildCosts?.structures}
          activeAttackArrows={activeAttackArrows}
          activeDefendArrow={activeDefendArrow}
          isRoomStarted={isRoomStarted}
          canStartRoom={isRoomCreator && isRoomLobby}
          onStartRoom={handleStartRoom}
          isStartingRoom={isStartingRoom}
          readyPlayerCount={readyPlayerCount}
          totalPlayers={roomPlayers.length}
          bottomOffset={discordBottomOffset}
          onHeightChange={setMobileDockHeight}
        />
      )}
      <ContextPanel
        isMobile={isMobile}
        cellInfo={selectedCellInfo}
        onClose={() => setSelectedCellInfo(null)}
        nationColors={nationColors}
        nationLabels={nationLabels}
        bottomOffset={contextPanelBottomOffset}
      />
      <ArrowPanel
        activeAttackArrows={activeAttackArrows}
        activeDefendArrow={activeDefendArrow}
        onReinforceArrow={handleReinforceArrow}
        onRetreatArrow={handleRetreatArrow}
        onClearArrow={handleClearActiveArrow}
        isMobile={isMobile}
        topOffset={arrowPanelTopOffset}
        bottomOffset={arrowPanelBottomOffset}
      />
      {/* The join/login modal now appears only if the map is loaded */}
      <Modal
        showLoginModal={isMapLoaded && !authLoading && (!userId || !hasJoined)}
        onJoinSubmit={handleJoinSubmit}
        onLogin={() => loginWithGoogle(`/rooms/${id}`)}
        isAuthenticated={!!userId}
        joinCode={joinCode}
        setJoinCode={setJoinCode}
        joinError={joinError}
        isJoining={isJoining}
        profile={profile}
        actionModal={actionModal}
        setActionModal={setActionModal}
        onBuildCity={handleBuildCity}
        config={config}
        userState={userState}
      />
    </div>
  );
};

export default Game;
